import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider, RequireAuth } from './shared/AuthProvider';
import { TenantRoute } from './shared/TenantRoute';
import { SuperAdminPortal } from './super-admin/SuperAdminPortal';
import { TenantPortal } from './tenant-portal/TenantPortal';
import { Landing } from './Landing';
import { authService, AUTH_METHOD_STORAGE_KEY } from './services/authService';
import { ApiError } from './services/apiClient';
import { unwrapResponse } from './services/apiClient';
import { setTokens } from './services/adminApi';
import { resolveOnboardingError } from './services/onboardingErrors';

// ── Module-level OAuth code extraction ────────────────────────────────────────
// OAuth auth codes are SINGLE-USE. React StrictMode double-mounts components
// and re-runs useEffect, causing the code to be sent to Entra twice
// (AADSTS54005). useSearchParams() reads from React Router's cached state,
// which is NOT cleared by history.replaceState — so in-component guards fail.
//
// Fix: extract the code ONCE at module load (before React renders anything),
// store it in a module-scoped variable, and immediately clear the URL.
// The AuthCallback component reads from this variable; on any StrictMode
// re-mount the variable is already null so no duplicate exchange occurs.
//
// NOTE: The callback route is /login/complete (NOT /auth/callback) to avoid
// Azure Static Web Apps intercepting the request as a built-in auth route.
let _pendingOAuthCode: string | null = null;
let _pendingOAuthState: string | null = null;
let _pendingOAuthError: string | null = null;
let _pendingOAuthErrorDesc: string | null = null;
let _oauthCallbackHandled = false;

(function extractOAuthParams() {
  const params = new URLSearchParams(window.location.search);
  if (window.location.pathname === '/login/complete') {
    _pendingOAuthCode = params.get('code');
    _pendingOAuthState = params.get('state');
    _pendingOAuthError = params.get('error');
    _pendingOAuthErrorDesc = params.get('error_description');
    // Strip query string from URL immediately — before React even mounts
    if (_pendingOAuthCode || _pendingOAuthError) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }
})();

// ── Module-level magic link token extraction ─────────────────────────────────
// Same pattern as OAuth: extract once before React mounts, strip query string.
let _pendingMagicToken: string | null = null;
let _pendingMagicTenantSlug: string | null = null;

(function extractMagicLinkParams() {
  if (window.location.pathname === '/verify') {
    const params = new URLSearchParams(window.location.search);
    _pendingMagicToken = params.get('token');
    _pendingMagicTenantSlug = params.get('tenantSlug');
    if (_pendingMagicToken) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }
})();

// ── Magic link verification handler ──────────────────────────────────────────
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

type SelfOnboardingResult = {
  success: boolean;
  tenantId: string;
  tenantSlug: string;
  companyName: string;
  authType: string;
  subscriptionState: string;
  planExpiry?: string;
  invitationEmailSent: boolean;
  invitationEmailError?: string;
  provisioningWarning?: string;
};

type SlugAvailabilityResult = {
  available: boolean;
  slug: string;
  suggestions: string[];
};

const createOnboardingSessionId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `onboarding-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
};

const normalizeSlug = (value: string): string => value
  .toLowerCase()
  .trim()
  .replace(/\s+/g, '-')
  .replace(/[^a-z0-9-]/g, '')
  .replace(/-+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 50);

const SelfOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = React.useState('');
  const [desiredSlug, setDesiredSlug] = React.useState('');
  const [slugEditedByUser, setSlugEditedByUser] = React.useState(false);
  const [adminEmail, setAdminEmail] = React.useState('');
  const [adminName, setAdminName] = React.useState('');
  const [signInMethod, setSignInMethod] = React.useState<'Dual' | 'SSOPreferred' | 'MagicLinkOnly'>('Dual');
  const [acceptedTerms, setAcceptedTerms] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [result, setResult] = React.useState<SelfOnboardingResult | null>(null);
  const [slugChecking, setSlugChecking] = React.useState(false);
  const [slugAvailable, setSlugAvailable] = React.useState<boolean | null>(null);
  const [slugSuggestions, setSlugSuggestions] = React.useState<string[]>([]);
  const [slugMessage, setSlugMessage] = React.useState('');
  const [submitCount, setSubmitCount] = React.useState(0);
  const onboardingSessionIdRef = React.useRef<string>(createOnboardingSessionId());

  React.useEffect(() => {
    if (slugEditedByUser) {
      return;
    }

    setDesiredSlug(normalizeSlug(companyName));
  }, [companyName, slugEditedByUser]);

  React.useEffect(() => {
    if (!desiredSlug.trim()) {
      setSlugAvailable(null);
      setSlugSuggestions([]);
      setSlugMessage('');
      return;
    }

    const normalized = normalizeSlug(desiredSlug);
    if (normalized.length < 3) {
      setSlugAvailable(null);
      setSlugSuggestions([]);
      setSlugMessage('Slug must be at least 3 characters.');
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      setSlugChecking(true);
      setSlugMessage('');
      try {
        const response = await fetch(`${API_BASE_URL}/api/onboarding/tenant/slug?slug=${encodeURIComponent(normalized)}`, {
          signal: controller.signal,
        });
        const payload = await response.json().catch(() => ({}));
        const unwrapped = unwrapResponse<SlugAvailabilityResult>(payload);
        setSlugAvailable(unwrapped.available);
        setSlugSuggestions(unwrapped.suggestions || []);
        setSlugMessage(unwrapped.available ? 'Slug is available.' : 'Slug is already taken.');
      } catch (err: unknown) {
        if ((err as { name?: string }).name === 'AbortError') {
          return;
        }
        setSlugAvailable(null);
        setSlugSuggestions([]);
        setSlugMessage('Unable to validate slug right now.');
      } finally {
        setSlugChecking(false);
      }
    }, 350);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [desiredSlug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyName.trim() || !adminEmail.trim()) {
      setError('Company name and admin email are required.');
      return;
    }
    if (!acceptedTerms) {
      setError('You must accept terms to continue.');
      return;
    }

    setLoading(true);
    setSubmitCount((prev) => prev + 1);
    setError('');
    try {
      const desired = desiredSlug.trim() ? normalizeSlug(desiredSlug) : '';
      const resp = await fetch(`${API_BASE_URL}/api/onboarding/tenant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: companyName.trim(),
          desiredSlug: desired || undefined,
          adminEmail: adminEmail.trim().toLowerCase(),
          adminName: adminName.trim() || undefined,
          preferredSignInMethod: signInMethod,
          acceptedTerms,
          evaluationDays: 30,
          retryAttempt: submitCount + 1,
          clientSessionId: onboardingSessionIdRef.current,
        }),
      });

      const payload = await resp.json().catch(() => ({}));
      const unwrapped = unwrapResponse<SelfOnboardingResult>(payload);
      setResult(unwrapped);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        const resolved = resolveOnboardingError(err);
        if (resolved.kind === 'conflict') {
          if (resolved.suggestions.length > 0) {
            setSlugAvailable(false);
            setSlugSuggestions(resolved.suggestions);
            setSlugMessage('Slug is already taken. Pick one of the suggested alternatives.');
          }
          if (resolved.slug) {
            setDesiredSlug(resolved.slug);
          }
          setError(resolved.message);
          return;
        }

        if (resolved.kind === 'evaluation-disabled') {
          setError(`${resolved.message} If you already have an invitation, use the sign-in flow on the home page.`);
          return;
        }

        setError(resolved.message);
        return;
      }

      setError((err as { message?: string })?.message || 'Failed to create onboarding tenant.');
    } finally {
      setLoading(false);
    }
  };

  if (result?.success) {
    return (
      <div className="onboarding-screen">
        <div className="onboarding-wrapper">
          <div className="onboarding-header">
            <h1>Zurvit</h1>
          </div>
          <div className="onboarding-success-card">
            <div className="onboarding-success-icon">✅</div>
            <h2>Tenant created</h2>
            <p>
              We created <strong>{result.companyName}</strong> (<code>{result.tenantSlug}</code>) with an evaluation subscription.
            </p>
            <p>
              Invitation email status: {result.invitationEmailSent ? 'sent' : 'not sent'}
            </p>
            {!result.invitationEmailSent && result.invitationEmailError && (
              <p style={{ color: 'var(--color-critical)' }}>
                Invitation issue: {result.invitationEmailError}
              </p>
            )}
            {result.provisioningWarning && (
              <p>
                Provisioning note: {result.provisioningWarning}
              </p>
            )}
            <p>
              Auth policy: <strong>{result.authType}</strong> | Subscription state: <strong>{result.subscriptionState}</strong>
            </p>
            <button onClick={() => navigate('/', { replace: true })} className="onboarding-button onboarding-button-primary">
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-screen">
      <div className="onboarding-wrapper">
        <div className="onboarding-header">
          <h1>Zurvit</h1>
          <p>Create your tenant (evaluation)</p>
        </div>
        <form onSubmit={handleSubmit} className="onboarding-form">
          <div className="onboarding-form-group">
            <label>Company name</label>
            <input 
              className="onboarding-input"
              value={companyName} 
              onChange={(e) => setCompanyName(e.target.value)} 
              disabled={loading} 
              placeholder="Enter your company name"
            />
          </div>
          <div className="onboarding-form-group">
            <label>Zurvit reference</label>
            <input 
              className="onboarding-input"
              value={desiredSlug} 
              onChange={(e) => {
                const value = e.target.value;
                setDesiredSlug(value);
                setSlugEditedByUser(value.trim().length > 0);
              }} 
              disabled={loading} 
              placeholder="e.g., acme-corp"
            />
            {desiredSlug.trim() && (
              <div className={`onboarding-helper-text ${slugChecking ? 'checking' : slugAvailable === false ? 'unavailable' : 'available'}`}>
                {slugChecking ? 'Checking slug availability...' : slugMessage || `Slug preview: ${normalizeSlug(desiredSlug)}`}
              </div>
            )}
            {!slugChecking && slugAvailable === false && slugSuggestions.length > 0 && (
              <div className="onboarding-suggestions">
                {slugSuggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => {
                      setDesiredSlug(suggestion);
                      setSlugEditedByUser(true);
                    }}
                    disabled={loading}
                    className="onboarding-suggestion-btn"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="onboarding-form-group">
            <label>Admin email</label>
            <input 
              className="onboarding-input"
              type="email" 
              value={adminEmail} 
              onChange={(e) => setAdminEmail(e.target.value)} 
              disabled={loading} 
              placeholder="admin@company.com"
            />
          </div>
          <div className="onboarding-form-group">
            <label>Admin name (optional)</label>
            <input 
              className="onboarding-input"
              value={adminName} 
              onChange={(e) => setAdminName(e.target.value)} 
              disabled={loading} 
              placeholder="John Doe"
            />
          </div>
          <div className="onboarding-form-group">
            <label>Initial sign-in policy</label>
            <select 
              className="onboarding-select"
              value={signInMethod} 
              onChange={(e) => setSignInMethod(e.target.value as any)} 
              disabled={loading}
            >
              <option value="Dual">Dual (SSO + magic link)</option>
              <option value="SSOPreferred">SSO preferred</option>
              <option value="MagicLinkOnly">Magic link only</option>
            </select>
            <div className="onboarding-helper-text">
              Note: evaluation signup never starts in SSO-required mode. Tenant admins can transition auth policy after onboarding.
            </div>
          </div>
          <div className="onboarding-checkbox-group">
            <input 
              type="checkbox" 
              id="accept-terms"
              className="onboarding-checkbox"
              checked={acceptedTerms} 
              onChange={(e) => setAcceptedTerms(e.target.checked)} 
              disabled={loading} 
            />
            <label htmlFor="accept-terms" className="onboarding-checkbox-label">
              I accept the platform terms for evaluation signup.
            </label>
          </div>
          {error && <p className="onboarding-error">{error}</p>}
          {submitCount > 1 && !error && !loading && (
            <p className="onboarding-retry-message">
              Retried {submitCount - 1} time{submitCount - 1 === 1 ? '' : 's'}.
            </p>
          )}
          <div className="onboarding-button-group">
            <button
              type="submit"
              disabled={loading || slugChecking || slugAvailable === false}
              className="onboarding-button onboarding-button-primary"
            >
              {loading ? 'Creating...' : 'Create tenant'}
            </button>
            <button 
              type="button" 
              onClick={() => navigate('/', { replace: true })} 
              disabled={loading} 
              className="onboarding-button onboarding-button-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MagicLinkVerify: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | null>(null);
  const [token, setToken] = React.useState<string | null>(null);
  const [tenantSlug, setTenantSlug] = React.useState<string | null>(null);
  const [verifying, setVerifying] = React.useState(false);

  useEffect(() => {
    const tokenValue = _pendingMagicToken;
    const tenantSlugValue = _pendingMagicTenantSlug;
    _pendingMagicToken = null;
    _pendingMagicTenantSlug = null;

    if (!tokenValue) {
      setError('No sign-in token found. Request a new magic link and try again.');
      return;
    }

    setToken(tokenValue);
    setTenantSlug(tenantSlugValue);
  }, []);

  const handleVerify = async () => {
    if (!token) {
      setError('No sign-in token found. Request a new magic link and try again.');
      return;
    }

    setVerifying(true);
    setError(null);
    const verifyUrl = `${API_BASE_URL}/api/auth/magic-link/verify?token=${encodeURIComponent(token)}${tenantSlug ? `&tenantSlug=${encodeURIComponent(tenantSlug)}` : ''}`;

    try {
      const response = await fetch(verifyUrl);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const unwrapped = unwrapResponse<any>(body);
        throw new Error(unwrapped?.error || 'Invalid or expired sign-in link. Request a new email link and try again.');
      }

      const data = await response.json();
      const result = unwrapResponse<any>(data);

      if (!result.success && !result.token) {
        throw new Error(result.error || 'Verification failed');
      }

      // Store tokens in both localStorage (adminApi) and sessionStorage (authService)
      if (result.token) {
        setTokens({ token: result.token, refreshToken: result.refreshToken });
      }

      // Also hydrate authService state so AuthProvider picks it up
      if (result.token && result.user) {
        const authState = {
          token: result.token,
          refreshToken: result.refreshToken,
          user: result.user,
        };
        sessionStorage.setItem('auth_state', JSON.stringify(authState));
        authService.adoptSessionTokens(result.token, result.refreshToken);
        authService.adoptUser(result.user);
        localStorage.setItem(AUTH_METHOD_STORAGE_KEY, 'magic_link');
      }

      // Redirect to the appropriate portal based on verified user claims.
      const userRoles: string[] = result?.user?.roles || [];
      const userTenantSlug: string | undefined = result?.user?.tenantSlug || tenantSlug || undefined;
      const isPlatformAdmin = userRoles.includes('PlatformAdmin');

      if (!isPlatformAdmin && userTenantSlug) {
        navigate(`/${userTenantSlug}/dashboard`, { replace: true });
      } else {
        navigate('/admin', { replace: true });
      }
    } catch (err: any) {
      console.error('Magic link verification failed:', err);
      setError(err?.message || 'Failed to verify sign-in link. Request a new link and try again.');
    } finally {
      setVerifying(false);
    }
  };

  if (error) {
    return (
      <div className="login-page">
        <div className="verify-card">
          <div className="error-icon" style={{ fontSize: '2rem', marginBottom: '1rem' }}>!</div>
          <p style={{ color: 'var(--color-severity-critical, #dc2626)', fontWeight: 600, textAlign: 'center' }}>Sign-in link verification failed</p>
          <p style={{ color: 'var(--color-text-secondary, #666)', marginTop: '0.5rem', textAlign: 'center' }}>{error}</p>
          <button
            onClick={() => navigate('/', { replace: true })}
            className="btn-secondary"
            style={{ marginTop: '1.5rem', width: 'auto', textAlign: 'center', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginInline: 'auto' }}
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      {verifying ? (
        <div className="verify-card">
          <div className="loading-spinner" />
          <p style={{ textAlign: 'center' }}>Verifying sign-in link...</p>
        </div>
      ) : (
        <div className="verify-card">
          <h2 style={{ marginBottom: '0.5rem', textAlign: 'center' }}>Confirm sign-in</h2>
          <p style={{ color: 'var(--color-text-secondary, #666)', marginBottom: '1rem', textAlign: 'center' }}>
            Click continue to verify your email link. This helps prevent link scanners from consuming your sign-in token.
          </p>
          <button
            onClick={handleVerify}
            className="btn-primary"
            style={{ width: 'auto', minWidth: '180px', textAlign: 'center', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginInline: 'auto' }}
          >
            Continue sign-in
          </button>
        </div>
      )}
    </div>
  );
};

// ── OAuth callback handler ────────────────────────────────────────────────────
// Reads from the module-scoped variables above (populated once, consumed once).
// Handles standard login, and multi-tenant SSO picker when user belongs to multiple orgs.
const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | null>(null);
  const [ssoTenants, setSsoTenants] = React.useState<Array<{ tenantId: string; tenantSlug: string; tenantName: string }> | null>(null);
  const [ssoNonce, setSsoNonce] = React.useState<string | null>(null);
  const [ssoEmail, setSsoEmail] = React.useState<string | null>(null);
  const [pickingTenant, setPickingTenant] = React.useState(false);

  const resolvePreferredTenantSlug = async (
    email: string | undefined,
    callbackTenantSlug: string | undefined
  ): Promise<string | undefined> => {
    const normalizedCallbackSlug = (callbackTenantSlug || '').trim();
    if (normalizedCallbackSlug) {
      return normalizedCallbackSlug;
    }

    const lastTenantSlug = (localStorage.getItem('zurvit_last_tenant') || '').trim();
    const normalizedEmail = (email || '').trim().toLowerCase();
    if (!normalizedEmail) {
      return normalizedCallbackSlug || undefined;
    }

    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail }),
      });

      if (!resp.ok) {
        return normalizedCallbackSlug || undefined;
      }

      const payload = await resp.json();
      const result = unwrapResponse<any>(payload);
      const tenants: Array<{ tenantSlug: string; tenantStatus?: string }> = result?.tenants || [];
      const activeTenants = tenants.filter((t) =>
        !!t?.tenantSlug &&
        (t.tenantStatus || '').toLowerCase() === 'active'
      );

      const nonSystemTenants = activeTenants.filter((t) => t.tenantSlug.toLowerCase() !== 'system');
      if (nonSystemTenants.length === 0) {
        return activeTenants[0]?.tenantSlug || undefined;
      }

      if (lastTenantSlug) {
        const lastMatch = nonSystemTenants.find((t) => t.tenantSlug.toLowerCase() === lastTenantSlug.toLowerCase());
        if (lastMatch) {
          return lastMatch.tenantSlug;
        }
      }

      return nonSystemTenants[0].tenantSlug;
    } catch {
      return undefined;
    }
  };

  useEffect(() => {
    // Consume the code — set to null so any StrictMode re-mount is a no-op
    const code = _pendingOAuthCode;
    const stateParam = _pendingOAuthState;
    const errorParam = _pendingOAuthError;
    const errorDescription = _pendingOAuthErrorDesc;
    _pendingOAuthCode = null;
    _pendingOAuthState = null;
    _pendingOAuthError = null;
    _pendingOAuthErrorDesc = null;

    // StrictMode re-mount after callback has already been consumed.
    if (!code && !errorParam) {
      if (_oauthCallbackHandled) {
        return;
      }

      setError('No sign-in response was found. The login session may have expired or the callback URL was reopened. Please start sign-in again.');
      return;
    }

    _oauthCallbackHandled = true;

    // Handle Entra error responses (e.g. user cancelled, consent required)
    if (errorParam) {
      setError(errorDescription || errorParam);
      return;
    }

    let portalType: 'super-admin' | 'tenant-admin' | 'user' = 'user';
    let tenantSlug: string | undefined;

    try {
      if (stateParam) {
        const parsed = JSON.parse(stateParam);
        portalType = parsed.portalType || 'user';
        tenantSlug = parsed.tenantSlug;
      }
    } catch {
      // malformed state — fall through to default
    }

    authService.initialize(portalType, tenantSlug)
      .then(() => authService.handleCallback(code || undefined))
      .then(async (response: any) => {
        // Check for multi-tenant picker response
        if (response?.error === 'multiple_tenants' && response?.tenants?.length > 0) {
          setSsoTenants(response.tenants);
          setSsoNonce(response.ssoNonce);
          setSsoEmail(response.user?.email);
          return; // Don't navigate — show picker
        }

        // Successful single-tenant or super-admin login
        // Store tokens via adminApi's setTokens for cross-module compatibility
        const state = authService.getState();
        if (state.token) {
          setTokens({ token: state.token, refreshToken: state.refreshToken ?? '' });
        }

        const userRoles: string[] = response?.user?.roles || [];
        const isPlatformAdmin = userRoles.includes('PlatformAdmin');
        const callbackSlug = response?.user?.tenantSlug || tenantSlug;
        const preferredTenantSlug = portalType === 'super-admin'
          ? undefined
          : await resolvePreferredTenantSlug(response?.user?.email, callbackSlug);

        // Route by login intent first. A standard /login flow should not
        // force platform admins into /admin unless they explicitly used
        // the super-admin entry point.
        if (portalType === 'super-admin') {
          navigate('/admin', { replace: true });
        } else if (preferredTenantSlug) {
          // Store last-used tenant for future auto-routing
          localStorage.setItem('zurvit_last_tenant', preferredTenantSlug);
          navigate(`/${preferredTenantSlug}/dashboard`, { replace: true });
        } else if (isPlatformAdmin) {
          navigate('/admin', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      })
      .catch((err) => {
        console.error('Auth callback failed:', err);
        setError(err?.message || 'Authentication failed');
      });
  }, [navigate]);

  const handleTenantPick = async (tenant: { tenantSlug: string; tenantName: string }) => {
    if (!ssoNonce) {
      setError('Sign-in session expired. Please try again.');
      return;
    }
    setPickingTenant(true);
    setError(null);

    try {
      await authService.completeSsoTenantSelection(ssoNonce, tenant.tenantSlug);

      // Also store tokens via adminApi's setTokens for cross-module compatibility
      const state = authService.getState();
      if (state.token) {
        setTokens({ token: state.token, refreshToken: state.refreshToken ?? '' });
      }

      // Store last-used tenant
      localStorage.setItem('zurvit_last_tenant', tenant.tenantSlug);

      navigate(`/${tenant.tenantSlug}/dashboard`, { replace: true });
    } catch (err: any) {
      console.error('Tenant selection failed:', err);
      setError(err?.message || 'Failed to complete sign-in. Please try again.');
      setPickingTenant(false);
    }
  };

  // Multi-tenant picker view
  if (ssoTenants && ssoTenants.length > 0) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <div className="logo">Zurvit</div>
            <p className="login-subtitle">Sign in to your account</p>
          </div>

          <div className="login-content">
            <div className="auth-card auth-card--center">
            <h2>Choose an organization</h2>
            {ssoEmail && (
              <p className="hint">
                {ssoEmail} belongs to multiple organizations
              </p>
            )}

            {error && <p className="error-message">{error}</p>}

            {pickingTenant ? (
              <div>
                <div className="loading-spinner" />
                <p>Signing in...</p>
              </div>
            ) : (
              <div className="tenant-option-list">
                {ssoTenants.map((t) => (
                  <button
                    type="button"
                    className="tenant-option-btn"
                    key={t.tenantSlug}
                    onClick={() => handleTenantPick(t)}
                  >
                    <span className="tenant-option-name">{t.tenantName}</span>
                    <span className="tenant-option-slug">{t.tenantSlug}</span>
                  </button>
                ))}
              </div>
            )}

            <button
              type="button"
              className="btn-link"
              onClick={() => navigate('/', { replace: true })}
            >
              Start over
            </button>
          </div>
          </div>

          <div className="login-footer">
            <p>Having trouble? Contact your administrator</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="loading-screen">
        <div className="error-icon" style={{ fontSize: '2rem', marginBottom: '1rem' }}>!</div>
        <p style={{ color: 'var(--color-severity-critical, #dc2626)', fontWeight: 600 }}>Sign-in failed</p>
        <p style={{ color: 'var(--color-text-secondary, #666)', marginTop: '0.5rem', maxWidth: '400px' }}>{error}</p>
        <button
          onClick={() => navigate('/', { replace: true })}
          style={{ marginTop: '1.5rem', padding: '0.5rem 1.5rem', cursor: 'pointer' }}
        >
          Back to home
        </button>
      </div>
    );
  }

  return (
    <div className="loading-screen">
      <div className="loading-spinner" />
      <p>Completing sign-in...</p>
    </div>
  );
};

// ── Super-admin portal ────────────────────────────────────────────────────────
const SuperAdminApp: React.FC = () => (
  <AuthProvider portalType="super-admin">
    <RequireAuth>
      <SuperAdminPortal />
    </RequireAuth>
  </AuthProvider>
);

// ── Tenant portal (unified portal with role-based navigation) ───────────────
const TenantApp: React.FC = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  return (
    <AuthProvider portalType="user" tenantSlug={tenantSlug}>
      <TenantRoute>
        <TenantPortal />
      </TenantRoute>
    </AuthProvider>
  );
};

// ── Root landing page ─────────────────────────────────────────────────────
type LoginStep = 'email' | 'pick-tenant' | 'sign-in-method' | 'sending-link' | 'check-email';

const LoginLanding: React.FC = () => {
  const query = React.useMemo(() => new URLSearchParams(window.location.search), []);
  const tenantHint = React.useMemo(() => {
    const raw = query.get('tenant') || '';
    return raw.trim().toLowerCase();
  }, [query]);
  const emailHint = React.useMemo(() => {
    const raw = query.get('email') || '';
    return raw.trim().toLowerCase();
  }, [query]);
  const [rememberMe, setRememberMe] = React.useState(() => localStorage.getItem('zurvit-remember-me') === 'true');
  const [email, setEmail] = React.useState(() => {
    if (emailHint) {
      return emailHint;
    }
    if (localStorage.getItem('zurvit-remember-me') === 'true') {
      return localStorage.getItem('zurvit-remembered-email') || '';
    }
    return '';
  });
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [step, setStep] = React.useState<LoginStep>('email');
  const [tenants, setTenants] = React.useState<Array<{ tenantSlug: string; tenantName: string; tenantStatus: string; userStatus: string }>>([]);
  const [selectedTenant, setSelectedTenant] = React.useState<{ tenantSlug: string; tenantName: string } | null>(null);
  const [showBreakGlass, setShowBreakGlass] = React.useState(false);
  const [breakGlassReason, setBreakGlassReason] = React.useState('');
  const [breakGlassSent, setBreakGlassSent] = React.useState(false);

  const resolveTenantSlugForSso = async (emailForLookup: string): Promise<string | undefined> => {
    const normalizedEmail = emailForLookup.trim().toLowerCase();
    if (!normalizedEmail) {
      return undefined;
    }

    const resp = await fetch(`${API_BASE_URL}/api/auth/lookup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: normalizedEmail }),
    });

    if (!resp.ok) {
      return undefined;
    }

    const data = await resp.json();
    const result = unwrapResponse<any>(data);
    const found: Array<{ tenantSlug: string; tenantStatus: string }> = result?.tenants || [];
    const active = found.filter((t) => !!t?.tenantSlug && (t.tenantStatus || '').toLowerCase() === 'active');
    if (active.length === 0) {
      return undefined;
    }

    const lastTenant = (localStorage.getItem('zurvit_last_tenant') || '').trim().toLowerCase();
    const nonSystem = active.filter((t) => t.tenantSlug.toLowerCase() !== 'system');

    if (lastTenant) {
      const match = nonSystem.find((t) => t.tenantSlug.toLowerCase() === lastTenant)
        || active.find((t) => t.tenantSlug.toLowerCase() === lastTenant);
      if (match) {
        return match.tenantSlug;
      }
    }

    if (nonSystem.length > 0) {
      return nonSystem[0].tenantSlug;
    }

    return active[0].tenantSlug;
  };

  const handleEmailLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError('Please enter your email address');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    // Persist or clear remember-me preference
    if (rememberMe) {
      localStorage.setItem('zurvit-remember-me', 'true');
      localStorage.setItem('zurvit-remembered-email', trimmed);
    } else {
      localStorage.removeItem('zurvit-remember-me');
      localStorage.removeItem('zurvit-remembered-email');
    }

    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });

      if (!resp.ok) {
        throw new Error('Unable to look up your account');
      }

      const data = await resp.json();
      const result = unwrapResponse<any>(data);
      const found: Array<{ tenantSlug: string; tenantName: string; tenantStatus: string; userStatus: string }> = result?.tenants || [];

      if (found.length === 0) {
        setError('No organizations found for this email address');
      } else if (found.length === 1) {
        // Single tenant — go straight to sign-in method
        setTenants(found);
        setSelectedTenant(found[0]);
        setStep('sign-in-method');
      } else {
        // Multiple tenants — show picker
        setTenants(found);
        setStep('pick-tenant');
      }
    } catch (err: any) {
      setError(err?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleTenantSelect = (tenant: { tenantSlug: string; tenantName: string }) => {
    setSelectedTenant(tenant);
    setStep('sign-in-method');
  };

  const handleSendMagicLink = async () => {
    if (!selectedTenant) return;
    setStep('sending-link');
    setError('');

    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          tenantSlug: selectedTenant.tenantSlug,
          returnUrl: window.location.origin,
          clientType: 'web',
        }),
      });

      if (!resp.ok) {
        throw new Error('Failed to send sign-in link');
      }

      // Response is wrapped in IActionResult — unwrap it
      const data = await resp.json();
      unwrapResponse<any>(data);

      setShowBreakGlass(false);
      setBreakGlassReason('');
      setBreakGlassSent(false);
      setStep('check-email');
    } catch (err: any) {
      setError(err?.message || 'Failed to send sign-in link. Please try again.');
      setStep('sign-in-method');
    }
  };

  const handleSendBreakGlassMagicLink = async () => {
    if (!selectedTenant) return;

    const reason = breakGlassReason.trim();
    if (!reason) {
      setError('Break-glass reason is required.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          tenantSlug: selectedTenant.tenantSlug,
          returnUrl: window.location.origin,
          clientType: 'web',
          isBreakGlass: true,
          breakGlassReason: reason,
        }),
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const unwrapped = unwrapResponse<any>(data);
        throw new Error(unwrapped?.error || 'Break-glass sign-in request failed.');
      }

      unwrapResponse<any>(data);
      setBreakGlassSent(true);
      setStep('check-email');
    } catch (err: any) {
      setError(err?.message || 'Break-glass sign-in request failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleSsoLoginFromLanding = async () => {
    setLoading(true);
    setError('');

    const hintedTenantSlug = tenantHint || selectedTenant?.tenantSlug || undefined;
    let resolvedTenantSlug = hintedTenantSlug;

    try {
      const trimmedEmail = email.trim().toLowerCase();
      const canLookupByEmail = !!trimmedEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
      if (!resolvedTenantSlug && canLookupByEmail) {
        resolvedTenantSlug = await resolveTenantSlugForSso(trimmedEmail);
      }
    } catch {
      // Best-effort lookup only; proceed without a slug if lookup fails.
    }

    authService.initialize('user', resolvedTenantSlug)
      .then(() => authService.loginWithRedirect())
      .catch((err) => {
        console.error('SSO redirect failed:', err);
        setError('Failed to start Microsoft sign-in. Please try again.');
        setLoading(false);
      });
  };

  const resetFlow = () => {
    setStep('email');
    if (!rememberMe) setEmail('');
    setError('');
    setTenants([]);
    setSelectedTenant(null);
    setShowBreakGlass(false);
    setBreakGlassReason('');
    setBreakGlassSent(false);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <div className="logo">Zurvit</div>
          <p className="login-subtitle">Sign in to your account</p>
        </div>

        <div className="login-content">

        {/* Step 1: Email lookup + SSO */}
        {step === 'email' && (
          <form onSubmit={handleEmailLookup}>
            <h2>Sign in to your organization</h2>
            {tenantHint && (
              <p className="tenant-hint">
                Tenant hint detected: <strong>{tenantHint}</strong>
              </p>
            )}
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              placeholder="you@company.com"
              autoFocus
              disabled={loading}
            />
            <label className="remember-me-row" htmlFor="tenant-remember-me-login">
              <input
                id="tenant-remember-me-login"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => {
                  setRememberMe(e.target.checked);
                  if (!e.target.checked) {
                    localStorage.removeItem('zurvit-remember-me');
                    localStorage.removeItem('zurvit-remembered-email');
                  }
                }}
              />
              Remember me
            </label>
            {error && <p className="error-message">{error}</p>}
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Looking up...' : 'Continue with email'}
            </button>

            <div className="divider">
              <span>or</span>
            </div>

            <button
              type="button"
              className="btn-microsoft"
              onClick={handleSsoLoginFromLanding}
              disabled={loading}
            >
              <svg width="16" height="16" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
              Sign in with Microsoft
            </button>
          </form>
        )}

        {/* Step 2: Tenant picker (multiple tenants) */}
        {step === 'pick-tenant' && (
          <div className="auth-card auth-card--center">
            <h2>Choose an organization</h2>
            <p className="hint">
              {email.trim().toLowerCase()} belongs to multiple organizations
            </p>
            <div className="tenant-option-list">
              {tenants.map((t) => (
                <button
                  type="button"
                  className="tenant-option-btn"
                  key={t.tenantSlug}
                  onClick={() => handleTenantSelect(t)}
                >
                  <span className="tenant-option-name">{t.tenantName}</span>
                  <span className="tenant-option-slug">{t.tenantSlug}</span>
                </button>
              ))}
            </div>
            <button type="button" className="btn-link" onClick={resetFlow}>
              Use a different email
            </button>
          </div>
        )}

        {/* Step 3: Sign-in method — magic link only (SSO is on landing page) */}
        {step === 'sign-in-method' && selectedTenant && (
          <div className="auth-card auth-card--center">
            <h2>Sign in to {selectedTenant.tenantName}</h2>
            <p>{email.trim().toLowerCase()}</p>

            {error && <p className="error-message">{error}</p>}

            <button type="button" className="btn-primary" onClick={handleSendMagicLink}>
              Email me a sign-in link
            </button>

            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowBreakGlass((value) => !value);
                setError('');
              }}
            >
              {showBreakGlass ? 'Hide break-glass recovery' : 'TenantAdmin break-glass recovery'}
            </button>

            {showBreakGlass && (
              <div className="tenant-breakglass-panel">
                <p>
                  Use this only when Microsoft SSO is unavailable or misconfigured. Break-glass requests are security-audited.
                </p>
                <label>
                  Reason
                  <input
                    value={breakGlassReason}
                    onChange={(e) => setBreakGlassReason(e.target.value)}
                    placeholder="e.g. SSO outage, TenantAdmin recovery"
                    disabled={loading}
                  />
                </label>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={handleSendBreakGlassMagicLink}
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Request break-glass sign-in link'}
                </button>
              </div>
            )}

            <button type="button" className="btn-link" onClick={resetFlow}>
              Use a different email
            </button>
          </div>
        )}

        {/* Step 3b: Sending magic link */}
        {step === 'sending-link' && (
          <div className="auth-card auth-card--center">
            <div className="loading-spinner" />
            <p>Sending sign-in link...</p>
          </div>
        )}

        {/* Step 4: Check your email confirmation */}
        {step === 'check-email' && (
          <div className="magic-link-sent auth-card auth-card--center">
            <div className="icon">✉️</div>
            <h2>Check your email</h2>
            <p>We sent a sign-in link to {email.trim().toLowerCase()}</p>
            <p className="hint">
              Click the link in the email to sign in to <strong>{selectedTenant?.tenantName}</strong>. The link expires in 15 minutes.
            </p>
            {breakGlassSent && (
              <p className="error-message">
                Break-glass flow requested. This request is logged for security review.
              </p>
            )}
            <button type="button" className="btn-secondary" onClick={resetFlow}>
              Start over
            </button>
          </div>
        )}

        </div>

        <div className="login-footer">
          <p>Having trouble? Contact your administrator</p>
        </div>

      </div>
    </div>
  );
};

const LogoutLanding: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const runLogout = async () => {
      try {
        await authService.logout();
      } catch {
        if (!cancelled) {
          navigate('/login', { replace: true });
        }
      }
    };

    runLogout();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="auth-shell">
      <div className="auth-backdrop" />
      <div className="auth-container">
        <div className="auth-card auth-card--center">
          <div className="loading-spinner" />
          <p>Signing out...</p>
        </div>
      </div>
    </div>
  );
};

// ── Router ────────────────────────────────────────────────────────────────────
export const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* OAuth callback — must be outside any AuthProvider */}
        <Route path="/login/complete" element={<AuthCallback />} />

        {/* Magic link invitation verification */}
        <Route path="/verify" element={<MagicLinkVerify />} />

        {/* Super-admin portal */}
        <Route path="/admin/*" element={<SuperAdminApp />} />

        {/* Public landing page */}
        <Route path="/" element={<Landing />} />

        {/* Tenant login page */}
        <Route path="/login" element={<LoginLanding />} />

        {/* Frontend logout routes */}
        <Route path="/logout" element={<LogoutLanding />} />
        <Route path="/:tenantSlug/logout" element={<LogoutLanding />} />

        {/* Public self-onboarding */}
        <Route path="/onboarding" element={<SelfOnboarding />} />

        {/* Tenant portals — /:tenantSlug/(dashboard|alerts|admin/*|...) */}
        <Route path="/:tenantSlug/*" element={<TenantApp />} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;
